import { useRef, useState } from 'react';

import { useTranslation } from '@kc-console/core';

import type { RefObject } from 'react';

interface UsePopoverStateOptions {
  /**
   * Popover를 렌더링할 기준 엘리먼트의 ref (Controlled 모드)
   * Controlled 모드(open prop 사용 시)에서는 필수입니다.
   */
  anchorRef?: RefObject<HTMLElement | null>;

  /**
   * disabled 상태
   * true일 경우 Popover를 열 수 없습니다.
   */
  disabled?: boolean;

  /**
   * 상태 변경 시 호출되는 콜백
   * Controlled 모드에서 권장됩니다.
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * 외부에서 제어하는 열림/닫힘 상태 (Controlled 모드)
   * 이 prop이 제공되면 Controlled 모드로 동작합니다.
   */
  open?: boolean;
}

interface UsePopoverStateReturn {
  /**
   * 실제 사용할 anchor ref
   */
  anchorRef: RefObject<HTMLElement | null>;

  /**
   * Popover를 닫는 함수
   */
  closePopover: () => void;

  /**
   * 내부 anchor ref (Uncontrolled 모드용)
   * customInput이나 기본 input에 전달할 ref입니다.
   */
  internalAnchorRef: RefObject<HTMLElement | null>;

  /**
   * Controlled 모드 여부
   */
  isControlled: boolean;

  /**
   * 실제 사용할 열림/닫힘 상태
   */
  open: boolean;

  /**
   * Popover를 여는 함수
   */
  openPopover: () => void;

  /**
   * Popover 상태를 토글하는 함수
   */
  togglePopover: () => void;
}

/**
 * Popover의 Controlled/Uncontrolled 상태를 관리하는 커스텀 훅
 *
 * @description
 * - Controlled 모드: open prop과 anchorRef prop을 모두 제공하면 동작
 * - Uncontrolled 모드: open prop이 없으면 내부 state로 관리
 *
 * @example
 * ```tsx
 * // Uncontrolled 모드
 * const { open, internalAnchorRef, openPopover, closePopover } = usePopoverState({
 *   disabled: false
 * });
 *
 * // Controlled 모드
 * const buttonRef = useRef(null);
 * const { open, anchorRef, openPopover, closePopover } = usePopoverState({
 *   open: isOpen,
 *   onOpenChange: setIsOpen,
 *   anchorRef: buttonRef,
 * });
 * ```
 */
export const usePopoverState = (options: UsePopoverStateOptions): UsePopoverStateReturn => {
  const { t } = useTranslation();
  const { open: externalOpen, onOpenChange, anchorRef: externalAnchorRef, disabled } = options;

  // Controlled vs Uncontrolled 모드 감지
  const isControlled = externalOpen !== undefined;

  // Controlled 모드에서 anchorRef가 없으면 경고
  if (process.env.NODE_ENV !== 'production') {
    if (isControlled && !externalAnchorRef) {
      console.error(
        'usePopoverState: Controlled 모드(open prop 사용 시)에서는 anchorRef가 필수입니다.',
      );
    }
  }

  // Internal state (Uncontrolled 모드에서만 사용)
  const [internalOpen, setInternalOpen] = useState(false);
  const internalAnchorRef = useRef<HTMLElement | null>(null);

  // 실제 사용할 값 결정
  const open = isControlled ? externalOpen : internalOpen;
  const anchorRef = (
    isControlled ? externalAnchorRef : internalAnchorRef
  ) as RefObject<HTMLElement | null>;

  const openPopover = () => {
    if (disabled) return;

    if (isControlled) {
      onOpenChange?.(true);
    } else {
      setInternalOpen(true);
    }
  };

  const closePopover = () => {
    if (isControlled) {
      onOpenChange?.(false);
    } else {
      setInternalOpen(false);
    }
  };

  const togglePopover = () => {
    if (disabled) return;

    if (isControlled) {
      onOpenChange?.(!open);
    } else {
      setInternalOpen(prev => !prev);
    }
  };

  return {
    isControlled,
    open,
    anchorRef,
    openPopover,
    closePopover,
    togglePopover,
    internalAnchorRef,
  };
};
